import React, {
  forwardRef,
  useEffect,
  useCallback,
  useState,
  useRef,
} from "react";
import styled from "styled-components";
import { useSecureChat } from "./SecureChatContext";
import MessageBubble from "./MessageBubble";
import TypingIndicator from "./TypingIndicator";

const MessageList = forwardRef(
  (
    {
      messages,
      onLoadMore,
      hasMore,
      isLoadingMore,
      onEdit,
      onDelete,
      canEdit,
      canDelete,
    },
    ref
  ) => {
    const { currentUser, typingUsers, selectedChatId, chats } = useSecureChat();
    const currentChat = Array.isArray(chats)
      ? chats.find((c) => c._id === selectedChatId)
      : null;
    const isGroupChat = currentChat?.isGroupChat || false;

    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isNearBottom, setIsNearBottom] = useState(true);
    const scrollContainerRef = useRef(null);
    const messagesEndRef = useRef(null);

    // Group messages by date
    const groupMessagesByDate = (messages) => {
      const groups = {};

      messages.forEach((message) => {
        const date = new Date(message.createdAt).toDateString();
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(message);
      });

      return Object.entries(groups).map(([date, messages]) => ({
        date,
        messages: messages.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        ),
      }));
    };

    // Check if user is near bottom of scroll
    const checkScrollPosition = useCallback(() => {
      if (!scrollContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      setIsNearBottom(distanceFromBottom < 100);
      setShowScrollButton(distanceFromBottom > 200);
    }, []);

    // Handle scroll events
    const handleScroll = useCallback(() => {
      checkScrollPosition();

      // Load more messages when scrolling to top
      if (scrollContainerRef.current) {
        const { scrollTop } = scrollContainerRef.current;
        if (scrollTop === 0 && hasMore && !isLoadingMore) {
          onLoadMore?.();
        }
      }
    }, [hasMore, isLoadingMore, onLoadMore, checkScrollPosition]);

    // Scroll to bottom
    const scrollToBottom = useCallback(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    // Auto-scroll to bottom on new messages (only if user is near bottom)
    useEffect(() => {
      if (isNearBottom && messages.length > 0) {
        scrollToBottom();
      }
    }, [messages.length, isNearBottom, scrollToBottom]);

    // Check scroll position on mount and when messages change
    useEffect(() => {
      checkScrollPosition();
    }, [messages, checkScrollPosition]);

    // Get typing indicator for current chat
    const getTypingUsers = () => {
      if (!selectedChatId) return [];

      const chatTypingUsers = typingUsers[selectedChatId] || {};
      return Object.keys(chatTypingUsers).filter(
        (userId) => chatTypingUsers[userId] && userId !== currentUser._id
      );
    };

    const typingUserIds = getTypingUsers();
    const messageGroups = groupMessagesByDate(messages);

    if (messages.length === 0) {
      return (
        <Container ref={ref}>
          <EmptyContainer>
            <EmptyIcon>💬</EmptyIcon>
            <EmptyText>No messages yet</EmptyText>
            <EmptySubtext>
              Send a message to start the conversation
            </EmptySubtext>
          </EmptyContainer>
        </Container>
      );
    }

    return (
      <Container ref={ref}>
        <ScrollContainer ref={scrollContainerRef} onScroll={handleScroll}>
          {isLoadingMore && (
            <LoadingContainer>
              <LoadingText>Loading older messages...</LoadingText>
            </LoadingContainer>
          )}

          {messageGroups.map(({ date, messages: groupMessages }) => (
            <DateGroup key={date}>
              <DateDivider>
                <DateText>{formatDate(date)}</DateText>
              </DateDivider>

              {groupMessages.map((message, index) => {
                const prevMessage = index > 0 ? groupMessages[index - 1] : null;
                const showAvatar = shouldShowAvatar(
                  message,
                  prevMessage,
                  isGroupChat
                );
                const participants = currentChat?.users || [];
                const totalRecipients = Math.max(
                  (participants.length || 1) - 1,
                  0
                );
                const senderId = message?.sender?._id || message?.sender;
                const readerIds = Array.isArray(message?.readBy)
                  ? message.readBy.map((r) => (r && r._id ? r._id : r))
                  : [];
                const readersExcludingSender = new Set(
                  readerIds.filter((id) => id && id !== senderId)
                );
                const isOwn = senderId === currentUser._id;
                const isReadByAll =
                  totalRecipients > 0
                    ? readersExcludingSender.size >= totalRecipients
                    : readerIds.length > 1;
                const status = isOwn && isReadByAll ? "read" : "sent";

                return (
                  <MessageBubble
                    key={message._id}
                    message={message}
                    isOwn={isOwn}
                    showAvatar={showAvatar}
                    status={status}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    canEdit={canEdit?.(message)}
                    canDelete={canDelete?.(message)}
                  />
                );
              })}
            </DateGroup>
          ))}

          {typingUserIds.length > 0 && (
            <TypingIndicator userIds={typingUserIds} />
          )}

          <div ref={messagesEndRef} />
        </ScrollContainer>

        {showScrollButton && (
          <ScrollToBottomButton onClick={scrollToBottom}>
            <ScrollIcon>↓</ScrollIcon>
          </ScrollToBottomButton>
        )}
      </Container>
    );
  }
);

// Helper function to determine if avatar should be shown
const shouldShowAvatar = (message, prevMessage, isGroupChat) => {
  // Only show avatars in group chats
  if (!isGroupChat) return false;

  if (!prevMessage) return true;

  // Show avatar if different sender
  if (message.sender !== prevMessage.sender) return true;

  // Show avatar if time gap is more than 5 minutes
  const timeDiff =
    new Date(message.createdAt) - new Date(prevMessage.createdAt);
  return timeDiff > 5 * 60 * 1000; // 5 minutes
};

// Helper function to format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString([], {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
};

export default MessageList;

// Styled Components
const Container = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  background-color: transparent;
`;

const ScrollContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 16px 0;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: var(--color-surface);
  margin: 0 20px 16px 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--color-border);
`;

const LoadingText = styled.p`
  margin: 0;
  color: var(--color-text);
  font-size: 14px;
`;

const DateGroup = styled.div`
  margin-bottom: 16px;
`;

const DateDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 20px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background-color: var(--color-border);
    opacity: 0.5;
    z-index: 1;
  }
`;

const DateText = styled.span`
  background-color: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  z-index: 2;
  position: relative;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
  color: var(--color-text);
`;

const EmptyText = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  color: var(--color-text);
  font-weight: 600;
  font-family: "Merriweather", serif;
`;

const EmptySubtext = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 14px;
`;

const ScrollToBottomButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--color-surface);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background-color: var(--color-background);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ScrollIcon = styled.span`
  font-size: 16px;
  font-weight: 600;
`;
